import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Label } from "@repo/ui/Label/label";
import { Size } from "@repo/ui/helpers/helpers";
import { IconTrash } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, useState } from "react";
import { SettingsTable } from "../features/settingsPage/shared/settingsTable.js";
import { SettingsTableBodySection } from "../features/settingsPage/shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../features/settingsPage/shared/settingsTableDataCell.js";
import { SettingsTableEmptyBodyContent } from "../features/settingsPage/shared/settingsTableEmptyBodyContent.js";
import { SettingsTableHeaderCell } from "../features/settingsPage/shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../features/settingsPage/shared/settingsTableHeaderSection.js";

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  weekday: "long",
  day: "2-digit",
  year: "numeric",
  timeZone: "utc",
});

interface DatesTableProps {
  dates: string[];
  onChange: (dates: string[]) => void;
  title: string;
  description: string;
  isDisabled?: boolean;
}

export const DatesTable: FC<DatesTableProps> = ({
  dates,
  onChange,
  title,
  description,
  isDisabled = false,
}) => {
  const [selectedDate, setSelectedDate] = useState("");
  const [tomorrow] = useState(() =>
    DateTime.now()
      .startOf("day")
      .plus({ days: 1 })
      .setZone("utc")
      .toFormat("yyyy-MM-dd"),
  );

  const addDate = () => {
    if (!selectedDate) {
      return;
    }

    if (dates.includes(selectedDate)) {
      setSelectedDate("");
      return;
    }

    const newDates = [...dates, selectedDate].toSorted();
    onChange(newDates);
    setSelectedDate("");
  };

  const removeDate = (date: string) => {
    onChange(dates.filter((d) => d !== date));
  };

  return (
    <div className="mt-6 space-y-2">
      <Label size={Size.SMALL} className="mb-2 block">
        {title}
      </Label>
      <SettingsTable>
        <SettingsTableHeaderSection>
          <tr>
            <SettingsTableHeaderCell className="w-full">
              Date
            </SettingsTableHeaderCell>
          </tr>
        </SettingsTableHeaderSection>
        <SettingsTableBodySection>
          {dates.length === 0 ? (
            <SettingsTableEmptyBodyContent colSpan={1}>
              No dates
            </SettingsTableEmptyBodyContent>
          ) : (
            dates.map((date) => (
              <tr key={date} className="group relative">
                <SettingsTableDataCell>
                  {/* Using `+ "T00:00:00.000Z"` to ensure the date is displayed correctly regardless of the user timezone */}
                  {dateFormatter.format(new Date(date + "T00:00:00.000Z"))}

                  <div className="absolute top-1/2 right-3 hidden -translate-y-1/2 cursor-pointer text-muted-foreground group-hover:block">
                    <IconButton
                      variant={ButtonVariant.Secondary}
                      icon={<IconTrash className="h-4 w-4" />}
                      onClick={() => removeDate(date)}
                      isDisabled={isDisabled}
                    />
                  </div>
                </SettingsTableDataCell>
              </tr>
            ))
          )}
        </SettingsTableBodySection>
      </SettingsTable>
      <div className="flex items-center space-x-2">
        <input
          type="date"
          value={selectedDate}
          min={tomorrow}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="block w-[180px] rounded-md border-0 py-1.5 text-foreground shadow-xs ring-1 ring-border-strong ring-inset focus:ring-2 focus:ring-indigo-500 focus:ring-inset sm:text-sm sm:leading-6"
          disabled={isDisabled}
        />
        <Button
          variant={ButtonVariant.Secondary}
          onClick={addDate}
          isDisabled={
            !selectedDate || dates.includes(selectedDate) || isDisabled
          }
        >
          Add date
        </Button>
      </div>
      <div className="text-sm text-gray-600">{description}</div>
    </div>
  );
};
